import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { resolveRecipeTitle } from '../../../utils/recipeTitle';
import { displayLanguageOf } from '../../../utils/recipeLanguage';
import { trackEvent } from '../../../services/analyticsService';

type ReaderLanguage = 'en' | 'fr';

export interface TitledRecipe {
  id?: string | number;
  title: string;
  translatedTitles?: { en?: string; fr?: string };
  originalLanguage?: 'en' | 'fr' | 'unknown';
  isTranslated?: boolean;
}

// The slice's field, read directly: `languageSlice` pulls in expo-localization
// (ESM), which jest cannot parse, and these helpers are unit tested.
const selectLanguage = (state: { language: { currentLanguage: ReaderLanguage } }) =>
  state.language.currentLanguage;

/**
 * A planned meal's title in the reader's language.
 *
 * The plan holds a copy of the recipe that `useMealPlanTranslation` rewrites
 * in place, so `recipe.title` is only right once that pass has reached this
 * meal. `translatedTitles` travels with the copy and is right from the first
 * paint — a Wednesday in next week's bucket stayed English for a whole
 * session because the pass never reached it (#737).
 */
export function plannedMealTitle(recipe: TitledRecipe, language: ReaderLanguage): string {
  return resolveRecipeTitle(recipe, language);
}

/** True when what the row is about to print is not the reader's language. */
export function isShownInWrongLanguage(recipe: TitledRecipe, language: ReaderLanguage): boolean {
  if (recipe.translatedTitles?.[language]?.trim()) return false;
  const shown = displayLanguageOf(recipe as any);
  return shown !== 'unknown' && shown !== language;
}

const reported = new Set<string>();

/**
 * The title to print, plus the `planned_meal_untranslated` event when it is
 * the wrong language — once per recipe and language per session. That event
 * is the price of never hiding a planned meal (plan A in the #737 spec) made
 * visible.
 */
export function usePlannedMealTitle(recipe: TitledRecipe, surface: string): string {
  const language = useSelector(selectLanguage);
  const wrong = isShownInWrongLanguage(recipe, language);
  const recipeId = String(recipe.id ?? '');
  useEffect(() => {
    if (!wrong) return;
    const key = `${recipeId}:${language}`;
    if (reported.has(key)) return;
    reported.add(key);
    trackEvent('planned_meal_untranslated', { recipe_id: recipeId, language, surface });
  }, [wrong, recipeId, language, surface]);
  return plannedMealTitle(recipe, language);
}
